import { asyncTimeout } from "@use/async-timeout";

import { CourseModel } from "@courses-models/course";
import { ProgressionModel } from "@courses-models/progression";
import { getPortals } from "@courses-pm2/api";
import {
  authenticateUser,
  redirectToLoginConnector,
} from "@courses-utils/gateway-auth";
import { applyAuthzInterceptor } from "@pm2/api";

import { loginConnector } from "@apps/viewer/src/ts/config";
import { Store } from "@apps/viewer/src/ts/store/Store";
import { UserTypes } from "@courses-models/user";

/**
 * Cache used to prevent re-creating a store over and over again.
 */
let cache: Promise<Store>;

/**
 * Helper that'll create a new instance of the Store by fetching data about
 * the currently authenticated user.
 */
export const createStore = async (courseId: string) => {
  if (!cache) {
    cache = new Promise<Store>(async (resolve, reject) => {
      try {
        // Start by authenticating the user, so that we can safely
        // interact with apis through Gateway v3 later on
        const user = await authenticateUser(loginConnector.profile);

        // If user isn't signed in, then redirect him to the
        // LoginConnector and wait
        if (!user) {
          redirectToLoginConnector(
            loginConnector.profile,
            loginConnector.signIn,
          );

          // Wait up to one minute for the user to be redirected (if
          // it takes longer than this, something went wrong!)
          await asyncTimeout(60000);

          // If we get here, throw an error
          throw new Error("createStore(): Unable to sign user in!");
        }

        // Fetch the course itself, so data is instantly available when
        // the application is rendering
        const [course, portals] = await Promise.all([
          CourseModel.GET_BY_ID(courseId),
          getPortals(),
        ]);

        // Fetch user progression state within this course
        const progression = await ProgressionModel.GET_BY_LEARNING_OBJECT({
          userId: user.id,
          userInstitutionId: (await user.getInstitutionId("")) ?? "",
          productId: course.id,
          learningObject: {
            id: course.id,
            title: course.title,
            type: "courseV2",
          },
        });

        const userType = await user.getUserType(course.id);

        applyAuthzInterceptor({
          isAuthorized: true,
          userRole:
            userType === UserTypes.STUDENT
              ? "Student"
              : userType === UserTypes.TEACHER
                ? "Teacher"
                : "None",
          authenticationProvider: user.auth.profile,
        });

        // Resolve with a brand new store instance
        resolve(new Store({ course, portals, progression, user }));
      } catch (err) {
        // Reject external promise, if an exception was thrown during
        // execution
        reject(err);
      }
    });
  }

  return cache;
};
